import React from 'react';
import { createRoot } from 'react-dom/client';
import { Environment } from '@bblabs/module-library/constants';
import { autoInjectReactApp } from '../../helpers/documentIsReady';
import WebAppPlayer from './WebAppPlayer';
import { assureBoolean } from '@/functions/assureBoolean';

/** Global function - defined in script read out by vidEmbed */
declare function videoReportedPlay(): void;
/** Global function - defined in script read out by vidEmbed */
declare function videoReportedSecondWatched(seconds: number): void;
/** Global function - defined in script read out by vidEmbed */
declare function videoReportedTimeWatched(seconds: number): void;
/** Global function - defined in script read out by vidEmbed */
declare function reportEndOfVideo(): void;

type InjectWebAppPlayerProps = {
  videoViewId?: string;
  videoId: string;
  jobId?: string | null;
  clientId: string;
  browserViewerId: string;
  thumbUrl: string;
  sources: { mime: string; url: string }[];
  reactionsEnabled?: boolean;
  useTracking?: boolean;
  subscriptionId: string;
  subscriptionStatus: string;
  ctaTitle: string;
  ctaLink: string;
  ctaColor: string;
  environment: Uppercase<Environment>;
  autoplay: boolean;
  useAspectRatio: number;
  ignorePoster: boolean;
  subtitleFileSrc: string;
  userFirstName: string;
};

function injectWebAppPlayer(container: HTMLElement, props: InjectWebAppPlayerProps | null) {
  if (props === null) {
    console.error('Could not find WebAppPlayer props');
    return;
  }

  const {
    videoId,
    thumbUrl,
    sources,
    clientId,
    subscriptionId,
    subscriptionStatus,
    ctaTitle,
    ctaLink,
    ctaColor,
    environment,
    autoplay,
    useAspectRatio,
    ignorePoster,
    subtitleFileSrc,
    userFirstName,
    videoViewId = undefined,
    browserViewerId,
    jobId = null,
    reactionsEnabled = false,
    useTracking = false,
  } = props;

  let metadata = undefined;
  if (videoViewId) {
    metadata = {
      browserViewerId,
      jobId: jobId || 'NONE',
      videoId,
      videoViewId,
    };
  }

  // Tracking handlers that used to be in VideoEmbed's template
  let lastSecondReported = -1;
  const lastTimeReported = -1;
  let eventListeners: { name: string; function: VoidFunction }[] = [];
  if (useTracking) {
    // These functions are executed in the VideoPlayer's scope
    const playEvent = function (this: { off: (e: string, h: VoidFunction) => void }) {
      videoReportedPlay();

      // Remove listener after first event fire
      this.off('play', playEvent);
    };

    const updateEvent = function (this: { currentTime: () => number }) {
      const time = this.currentTime();
      const seconds = Math.floor(time);
      // This event is triggered multiple times a second and is browser dependent, this check ensures 1 second events
      if (lastSecondReported !== seconds) {
        lastSecondReported = seconds;

        videoReportedSecondWatched(seconds);
      }
      if (lastTimeReported !== time) {
        videoReportedTimeWatched(time);
      }
    };

    const endedEvent = () => {
      if (typeof reportEndOfVideo === 'function') {
        reportEndOfVideo();
      }
    };

    eventListeners = [
      { name: 'timeupdate', function: updateEvent },
      { name: 'play', function: playEvent },
      { name: 'ended', function: endedEvent },
    ];
  }

  const root = createRoot(container);
  root.render(
    <WebAppPlayer
      thumbUrl={thumbUrl}
      sources={sources.map(({ url, mime }) => ({ src: url, type: mime }))}
      clientId={clientId}
      subscriptionId={subscriptionId}
      subscriptionStatus={subscriptionStatus}
      ctaTitle={ctaTitle}
      ctaLink={ctaLink}
      ctaColor={ctaColor}
      environment={environment}
      eventListeners={eventListeners}
      autoplay={autoplay}
      useAspectRatio={useAspectRatio}
      ignorePoster={ignorePoster}
      subtitleFileSrc={subtitleFileSrc}
      videoPlayMetadata={metadata}
      userFirstName={userFirstName}
      reactionsEnabled={assureBoolean(reactionsEnabled)}
    />,
  );
}

autoInjectReactApp('WebAppPlayer', injectWebAppPlayer);
